import { useState, useCallback, useEffect } from 'react';
import { notify } from 'src/utils/notification';
import {
  ForgotPasswordMethod,
  IOTPState,
  IVerifyOTPFailureData,
  IResendOTPSuccessData,
} from 'src/shared/interface/forgot-password';
import { resendOTP, verifyOTP } from 'src/api/forgot-password';
import { useCountdown } from 'src/hooks/useCountdown';
import {
  APP_CODE,
  DEFAULT_VALUE,
  DESTINATION_LABEL,
  ERROR_MESSAGES,
  LANGUAGE,
  MILLISECONDS_MULTIPLIER,
  OTP_MESSAGES,
  OTP_TYPE,
  SUCCESS_CODE,
} from 'src/shared/constants';
import { formatTime, getSecondsRemaining } from '../helper';
import { message } from 'antd';
import { ERROR_CODE } from 'src/shared/enum/errors_code.enum';

interface IUseOTPStepProps {
  method: ForgotPasswordMethod;
  username: string;
  identifier: string;
  otpState: IOTPState;
  onSuccess: (otp: string, token: string) => void;
  onOTPStateChange: (state: Partial<IOTPState>) => void;
}

interface IUseOTPStepReturn {
  loading: boolean;
  otpExpiryTimer: ReturnType<typeof useCountdown>;
  resendCooldownTimer: ReturnType<typeof useCountdown>;
  verifyLockTimer: ReturnType<typeof useCountdown>;
  resendLockTimer: ReturnType<typeof useCountdown>;
  handleOTPComplete: (otp: string) => Promise<void>;
  handleResendOTP: () => Promise<void>;
  canResend: boolean;
  canVerify: boolean;
  remainingVerifyAttempts: number;
}

const getOTPType = (method: ForgotPasswordMethod): number => {
  return method === ForgotPasswordMethod.EMAIL ? OTP_TYPE.EMAIL : OTP_TYPE.SMS;
};

export const useOTPStep = ({
  method,
  username,
  identifier,
  otpState,
  onSuccess,
  onOTPStateChange,
}: IUseOTPStepProps): IUseOTPStepReturn => {
  const [loading, setLoading] = useState(false);

  const otpExpiryTimer = useCountdown();
  const resendCooldownTimer = useCountdown();
  const verifyLockTimer = useCountdown();
  const resendLockTimer = useCountdown();

  // Initialize timers
  useEffect(() => {
    const expirySeconds = getSecondsRemaining(otpState.expiresAt);
    if (expirySeconds > DEFAULT_VALUE.MIN_TIMER_THRESHOLD) otpExpiryTimer.start(expirySeconds);

    if (!otpState.countdownTimeInSecond) {
      const cooldownSeconds = getSecondsRemaining(otpState.resendCooldownUntil);
      if (cooldownSeconds > DEFAULT_VALUE.MIN_TIMER_THRESHOLD)
        resendCooldownTimer.start(cooldownSeconds);
    }

    if (otpState.isVerifyLocked) {
      const lockSeconds = getSecondsRemaining(otpState.verifyLockUntil);
      if (lockSeconds > DEFAULT_VALUE.MIN_TIMER_THRESHOLD) verifyLockTimer.start(lockSeconds);
    }

    if (otpState.isResendLocked) {
      const lockSeconds = getSecondsRemaining(otpState.resendLockUntil);
      if (lockSeconds > DEFAULT_VALUE.MIN_TIMER_THRESHOLD) resendLockTimer.start(lockSeconds);
    }
    return () => {
      otpExpiryTimer.reset();
      resendCooldownTimer.reset();
      verifyLockTimer.reset();
      resendLockTimer.reset();
    };
  }, [otpState]);

  // Auto unlock verify
  useEffect(() => {
    if (
      verifyLockTimer.timeLeft === DEFAULT_VALUE.DEFAULT_VALUE_VERIFY_ATTEMPTS &&
      otpState.isVerifyLocked &&
      !otpState.isBlock
    ) {
      onOTPStateChange({
        isVerifyLocked: false,
        verifyLockUntil: null,
        verifyAttempts: DEFAULT_VALUE.DEFAULT_VALUE_VERIFY_ATTEMPTS,
      });
    }
  }, [verifyLockTimer.timeLeft, otpState.isVerifyLocked]);

  // Auto unlock resend
  useEffect(() => {
    if (
      resendLockTimer.timeLeft === DEFAULT_VALUE.DEFAULT_VALUE_VERIFY_ATTEMPTS &&
      otpState.isResendLocked
    ) {
      onOTPStateChange({
        isResendLocked: false,
        resendLockUntil: null,
        resendCount: DEFAULT_VALUE.DEFAULT_VALUE_VERIFY_ATTEMPTS,
      });
    }
  }, [resendLockTimer.timeLeft, otpState.isResendLocked]);

  useEffect(() => {
    if (!otpState.countdownTimeInSecond || otpState.countdownTimeInSecond <= 0) {
      return;
    }

    const interval = setInterval(() => {
      const currentCountdown = otpState.countdownTimeInSecond ?? 0;
      const newCountdown = Math.max(0, currentCountdown - 1);

      onOTPStateChange({
        countdownTimeInSecond: newCountdown,
      });

      if (newCountdown <= 0) {
        if (otpState.isBlock) {
          onOTPStateChange({
            isBlock: false,
            countdownTimeInSecond: 0,
            isVerifyLocked: false,
            verifyAttempts: 0,
          });
        } else {
          onOTPStateChange({
            countdownTimeInSecond: 0,
          });
        }
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [otpState.countdownTimeInSecond, otpState.isBlock, onOTPStateChange]);

  const canResend =
    !otpState.isResendLocked &&
    (!otpState.countdownTimeInSecond || otpState.countdownTimeInSecond <= 0) &&
    otpState.resendCount < otpState.maxResendCount;

  const canVerify = !otpState.isVerifyLocked && !otpState.isBlock && !loading;
  const remainingVerifyAttempts = otpState.maxVerifyAttempts - otpState.verifyAttempts;

  // Handle verify failure with API data
  const handleVerifyFailureWithAPIData = useCallback(
    (data: IVerifyOTPFailureData) => {
      const now = Date.now();
      const currentCounter = data.currentCounter ?? 0;
      const maxCounter = data.maxCounter ?? 0;
      const isNowLocked = currentCounter >= maxCounter;
      const lockDuration = data.lockVerifyInSecond || DEFAULT_VALUE.DEFAULT_VALUE_VERIFY_IN_SECOND;
      const remaining = maxCounter - currentCounter;

      onOTPStateChange({
        verifyAttempts: currentCounter,
        maxVerifyAttempts: maxCounter,
        isVerifyLocked: isNowLocked,
        verifyLockUntil: isNowLocked ? now + lockDuration * MILLISECONDS_MULTIPLIER : null,
      });

      if (isNowLocked) {
        verifyLockTimer.start(lockDuration);
        message.error(OTP_MESSAGES.VERIFY_LOCKED);
      } else {
        message.error(OTP_MESSAGES.VERIFY_INCORRECT(remaining));
      }
    },
    [onOTPStateChange, verifyLockTimer],
  );

  // Handle verify failure with client-side counting
  const handleVerifyFailureFallback = useCallback(() => {
    const now = Date.now();
    const newAttempts = otpState.verifyAttempts + 1;
    const maxAttempts = otpState.maxVerifyAttempts;
    const remaining = maxAttempts - newAttempts;
    const isNowLocked = newAttempts >= maxAttempts;

    if (isNowLocked) {
      const lockDuration = DEFAULT_VALUE.DEFAULT_VALUE_VERIFY_IN_SECOND;
      onOTPStateChange({
        verifyAttempts: newAttempts,
        isVerifyLocked: true,
        verifyLockUntil: now + lockDuration * MILLISECONDS_MULTIPLIER,
      });
      verifyLockTimer.start(lockDuration);
      message.error(OTP_MESSAGES.VERIFY_LOCKED);
      return;
    } else {
      onOTPStateChange({ verifyAttempts: newAttempts });
      message.error(OTP_MESSAGES.VERIFY_INCORRECT(remaining));
    }
  }, [otpState.verifyAttempts, otpState.maxVerifyAttempts, onOTPStateChange, verifyLockTimer]);

  const handleOTPComplete = useCallback(
    async (otp: string) => {
      if (!canVerify) return;

      setLoading(true);
      try {
        const res = await verifyOTP({
          appCode: APP_CODE,
          username,
          destAddress: identifier,
          type: getOTPType(method),
          otp,
        });

        if (res?.code === SUCCESS_CODE && res?.data?.token) {
          notify.success(OTP_MESSAGES.VERIFY_SUCCESS);
          onSuccess(otp, res.data.token);
          return;
        }

        const data = res?.data;
        const hasAPIData = data?.currentCounter !== undefined && data?.maxCounter !== undefined;

        if (hasAPIData) {
          handleVerifyFailureWithAPIData(data as IVerifyOTPFailureData);
        } else {
          handleVerifyFailureFallback();
        }
      } catch (error: any) {
        const errorResponse = error?.response?.data;

        console.log('error', error?.response?.data);
        const errorData = error?.response?.data?.data;
        const errorCode = errorResponse?.code;
        if (errorData) {
          // If blocked (wrong too many times)
          if (errorData.isBlock === true) {
            const countdown = errorData.countdownTimeInSecond ?? 300;
            const now = Date.now();

            onOTPStateChange({
              isBlock: true,
              countdownTimeInSecond: countdown,
              isVerifyLocked: true,
              verifyLockUntil: now + countdown * MILLISECONDS_MULTIPLIER,
              verifyAttempts: errorData.currentCounter ?? otpState.verifyAttempts + 1,
              maxVerifyAttempts: errorData.maxCounter ?? otpState.maxVerifyAttempts,
            });

            verifyLockTimer.start(countdown);
            return;
          }
          if (errorCode === ERROR_CODE.E283_AUTH && errorData.code === ERROR_CODE.OTP_413) {
            const remaining =
              (errorData.maxCounter ?? otpState.maxVerifyAttempts) -
              (errorData.currentCounter ?? otpState.verifyAttempts);

            onOTPStateChange({
              verifyAttempts: errorData.currentCounter ?? otpState.verifyAttempts + 1,
              maxVerifyAttempts: errorData.maxCounter ?? otpState.maxVerifyAttempts,
            });
            message.error(OTP_MESSAGES.VERIFY_EXPIRED(remaining));
            return;
          }

          if (errorData.currentCounter !== undefined && errorData.maxCounter !== undefined) {
            handleVerifyFailureWithAPIData(errorData as IVerifyOTPFailureData);
            return;
          }
        }
        if (!error?.response?.data?.code) {
          notify.error(ERROR_MESSAGES.DEFAULT);
        }
      } finally {
        setLoading(false);
      }
    },
    [
      canVerify,
      username,
      identifier,
      method,
      onSuccess,
      handleVerifyFailureWithAPIData,
      handleVerifyFailureFallback,
      otpState.verifyAttempts,
      otpState.maxVerifyAttempts,
      onOTPStateChange,
      verifyLockTimer,
    ],
  );

  // Handle successful resend
  const handleResendSuccess = useCallback(
    (data: IResendOTPSuccessData) => {
      const now = Date.now();
      const isNowLocked = data.currentCounter >= data.maxCounter || data.isBlock;
      const newExpiresAt = now + data.otpExpireIn * MILLISECONDS_MULTIPLIER;

      otpExpiryTimer.reset();
      otpExpiryTimer.start(data.otpExpireIn);

      onOTPStateChange({
        resendCount: data.currentCounter,
        maxResendCount: data.maxCounter,
        expiresAt: newExpiresAt,
        resendCooldownUntil: now + data.lockGetOtpInSecond * MILLISECONDS_MULTIPLIER,
        isResendLocked: isNowLocked,
        resendLockUntil: isNowLocked
          ? now + data.countdownTimeInSecond * MILLISECONDS_MULTIPLIER
          : null,
        verifyAttempts: DEFAULT_VALUE.DEFAULT_VALUE_VERIFY_ATTEMPTS,
        countdownTimeInSecond: data.lockGetOtpInSecond,
      });

      resendCooldownTimer.reset();
      resendLockTimer.reset();

      if (isNowLocked) {
        resendLockTimer.start(data.countdownTimeInSecond);
        resendCooldownTimer.reset();
        notify.warning(OTP_MESSAGES.RESEND_EXHAUSTED(formatTime(data.countdownTimeInSecond)));
      } else {
        resendCooldownTimer.reset();
        const destination =
          method === ForgotPasswordMethod.EMAIL ? DESTINATION_LABEL.EMAIL : DESTINATION_LABEL.SMS;
        notify.success(OTP_MESSAGES.RESEND_SUCCESS(destination, identifier));
      }
    },
    [method, identifier, onOTPStateChange, otpExpiryTimer, resendCooldownTimer, resendLockTimer],
  );

  const handleResendOTP = useCallback(async () => {
    if (!canResend) {
      if (otpState.isResendLocked) {
        notify.error(OTP_MESSAGES.RESEND_LOCKED);
      }
      return;
    }

    setLoading(true);
    try {
      const res = await resendOTP({
        appCode: APP_CODE,
        username,
        destAddress: identifier,
        type: getOTPType(method),
        language: LANGUAGE,
      });

      if (res?.code === SUCCESS_CODE && res?.data?.code === SUCCESS_CODE) {
        handleResendSuccess(res.data as unknown as IResendOTPSuccessData);
      } else {
        throw new Error(res?.data?.message || res?.message || OTP_MESSAGES.RESEND_ERROR);
      }
    } catch (error: any) {
      console.error('Resend OTP error:', error);

      const errorData = error?.response?.data?.data;
      if (
        errorData?.code === ERROR_CODE.OTP_410 ||
        errorData?.code === ERROR_CODE.OTP_412 ||
        errorData?.code === ERROR_CODE.OTP_411 ||
        errorData?.code === ERROR_CODE.OTP_409 ||
        errorData?.code === ERROR_CODE.OTP_413
      ) {
        const countdown = errorData.countdownTimeInSecond ?? errorData.lockGetOtpInSecond ?? 30;

        onOTPStateChange({
          countdownTimeInSecond: countdown,
        });

        notify.warning(errorData.message || 'Vui lòng thử lại sau');
        return;
      }

      notify.error(error?.message || 'Đã có lỗi xảy ra. Vui lòng thử lại sau');
    } finally {
      setLoading(false);
    }
  }, [canResend, username, identifier, method, otpState.isResendLocked, handleResendSuccess]);

  return {
    loading,
    otpExpiryTimer,
    resendCooldownTimer,
    verifyLockTimer,
    resendLockTimer,
    handleOTPComplete,
    handleResendOTP,
    canResend,
    canVerify,
    remainingVerifyAttempts,
  };
};
